import {
  Component,
  OnInit,
  signal,
  computed,
  inject
} from '@angular/core';

import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';


import {
  CookingType,
  CreateCookingType,
  UpdateCookingType
} from 'app/models/cookings/model';
import { CookingTypeService } from 'app/services/cookings/cooking-type.service ';


// ============================================================
// Filters
// ============================================================

export enum CookingTypeStatusFilter {
  All = 0,
  Active = 1,
  Inactive = 2
}


@Component({
  selector: 'app-cooking-type',

  standalone: true,

  imports: [
    CommonModule,
    ReactiveFormsModule
  ],

  templateUrl: './cooking-type.component.html',

  styleUrls: ['./cooking-type.component.scss']
})
export class CookingTypeComponent implements OnInit {

  // ============================================================
  // Enums exposed to HTML
  // ============================================================

  CookingTypeStatusFilter = CookingTypeStatusFilter;


  // ============================================================
  // Data State
  // ============================================================

  items = signal<CookingType[]>([]);

  isLoading = signal(false);

  errorMsg = signal<string | null>(null);


  // ============================================================
  // Search & Filters
  // ============================================================

  searchTerm = signal('');

  statusFilter = signal<CookingTypeStatusFilter>(
    CookingTypeStatusFilter.All
  );


  // ============================================================
  // Pagination
  // ============================================================

  currentPage = signal(1);

  itemsPerPage = signal(10);


  // ============================================================
  // Form Modal
  // ============================================================

  showFormModal = signal(false);

  editingId = signal<number | null>(null);

  saving = signal(false);


  // ============================================================
  // Delete Confirmation Modal
  // ============================================================

  showDeleteConfirm = signal(false);

  pendingDelete = signal<CookingType | null>(null);


  // ============================================================
  // Dependencies
  // ============================================================

  private readonly service = inject(CookingTypeService);

  private readonly fb = inject(FormBuilder);


  // ============================================================
  // Form
  // ============================================================

  form: FormGroup = this.fb.group({

    name_ar: [
      '',
      [
        Validators.required,
        Validators.maxLength(150)
      ]
    ],

    name_en: [
      '',
      [
        Validators.required,
        Validators.maxLength(150)
      ]
    ],

    status: [1, Validators.required]

  });


  // ============================================================
  // Filtered Items (client-side)
  // ============================================================

  filteredItems = computed<CookingType[]>(() => {

    let list = this.items();

    // ---------- Search ----------
    const term = (this.searchTerm() || '').trim().toLowerCase();

    if (term) {

      const normalizedTerm = this.normalizeArabicText(term);

      list = list.filter(x => {

        const enMatch =
          (x.name_en || '')
            .toLowerCase()
            .includes(term);

        const arMatch =
          this.normalizeArabicText(
            (x.name_ar || '').toLowerCase()
          ).includes(normalizedTerm);

        return enMatch || arMatch;

      });

    }

    // ---------- Status ----------
    switch (this.statusFilter()) {

      case CookingTypeStatusFilter.Active:
        list = list.filter(x => x.status === 1);
        break;

      case CookingTypeStatusFilter.Inactive:
        list = list.filter(x => x.status !== 1);
        break;

    }

    return list;

  });


  // ============================================================
  // Paged Items
  // ============================================================

  pagedItems = computed<CookingType[]>(() => {

    const start =
      (this.currentPage() - 1) *
      this.itemsPerPage();

    return this.filteredItems()
      .slice(start, start + this.itemsPerPage());

  });


  // ============================================================
  // Pagination Meta
  // ============================================================

  totalItems = computed(() => this.filteredItems().length);

  totalPages = computed(() =>
    Math.max(
      1,
      Math.ceil(this.totalItems() / this.itemsPerPage())
    )
  );

  startIndex = computed(() =>
    this.totalItems() === 0
      ? 0
      : (this.currentPage() - 1) * this.itemsPerPage() + 1
  );

  endIndex = computed(() =>
    Math.min(
      this.currentPage() * this.itemsPerPage(),
      this.totalItems()
    )
  );


  // ============================================================
  // Active Filters
  // ============================================================

  hasActiveFilters = computed(() =>
    this.statusFilter() !== CookingTypeStatusFilter.All ||
    (this.searchTerm() || '').trim().length > 0
  );


  // ============================================================
  // Init
  // ============================================================

  ngOnInit(): void {
    this.load();
  }


  // ============================================================
  // Load Data
  // ============================================================

  load(): void {

    this.isLoading.set(true);

    this.errorMsg.set(null);

    this.service.getAll().subscribe({

      next: (data) => {

        this.items.set(data ?? []);

        this.isLoading.set(false);

        // Reset to page 1 if current page is out of range
        if (this.currentPage() > this.totalPages()) {
          this.currentPage.set(1);
        }

      },

      error: (err) => {

        console.error('Failed to load cooking types:', err);

        this.errorMsg.set(
          err?.error?.message ??
          'Failed to load data. Please try again.'
        );

        this.items.set([]);

        this.isLoading.set(false);

      }

    });

  }


  // ============================================================
  // Filters & Search
  // ============================================================

  onSearch(event: Event): void {

    const term =
      ((event.target as HTMLInputElement).value || '').trim();

    this.searchTerm.set(term);

    this.currentPage.set(1);

  }


  clearSearch(): void {

    if (!this.searchTerm()) return;

    this.searchTerm.set('');

    this.currentPage.set(1);

  }


  onStatusFilterChange(event: Event): void {

    const value =
      Number((event.target as HTMLSelectElement).value);

    this.statusFilter.set(
      value as CookingTypeStatusFilter
    );

    this.currentPage.set(1);

  }


  resetFilters(): void {

    this.searchTerm.set('');

    this.statusFilter.set(CookingTypeStatusFilter.All);

    this.currentPage.set(1);

  }


  // ============================================================
  // Pagination
  // ============================================================

  goToPage(page: number): void {

    if (
      page < 1 ||
      page > this.totalPages() ||
      page === this.currentPage()
    ) {
      return;
    }

    this.currentPage.set(page);

    window.scrollTo({ top: 0, behavior: 'smooth' });

  }


  previousPage(): void {
    this.goToPage(this.currentPage() - 1);
  }


  nextPage(): void {
    this.goToPage(this.currentPage() + 1);
  }


  onItemsPerPageChange(event: Event): void {

    const value =
      Number((event.target as HTMLSelectElement).value);

    if (value === this.itemsPerPage()) return;

    this.itemsPerPage.set(value);

    this.currentPage.set(1);

  }


  getPageNumbers(): number[] {

    const pages: number[] = [];

    const maxVisiblePages = 5;

    const total = this.totalPages();

    if (total <= 1) return total === 1 ? [1] : [];

    let startPage =
      Math.max(1, this.currentPage() - Math.floor(maxVisiblePages / 2));

    let endPage =
      Math.min(total, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;

  }


  // ============================================================
  // Form Modal
  // ============================================================

  openCreate(): void {

    this.editingId.set(null);

    this.form.reset({
      name_ar: '',
      name_en: '',
      status: 1
    });

    this.errorMsg.set(null);

    this.showFormModal.set(true);

  }


  openEdit(item: CookingType): void {

    this.editingId.set(item.id);

    this.form.reset({
      name_ar: item.name_ar,
      name_en: item.name_en,
      status: item.status
    });

    this.errorMsg.set(null);

    this.showFormModal.set(true);

  }


  closeFormModal(): void {

    this.showFormModal.set(false);

    this.editingId.set(null);

    this.errorMsg.set(null);

  }


  save(): void {

    if (this.form.invalid) {

      this.form.markAllAsTouched();

      return;

    }

    this.saving.set(true);

    this.errorMsg.set(null);

    const id = this.editingId();

    const body = {

      name_ar: (this.form.value.name_ar || '').trim(),

      name_en: (this.form.value.name_en || '').trim(),

      status: Number(this.form.value.status)

    };

    const request$ = id
      ? this.service.update(id, body as UpdateCookingType)
      : this.service.create(body as CreateCookingType);

    request$.subscribe({

      next: () => {

        this.saving.set(false);

        this.closeFormModal();

        this.load();

      },

      error: (err) => {

        console.error('Save failed:', err);

        this.errorMsg.set(
          err?.error?.message ??
          'Failed to save. Please try again.'
        );

        this.saving.set(false);

      }

    });

  }


  // ============================================================
  // Status Toggle
  // ============================================================

  toggleStatus(item: CookingType): void {

    // Optimistic toggle
    const originalStatus = item.status;

    this.items.update(list =>
      list.map(x =>
        x.id === item.id
          ? { ...x, status: x.status === 1 ? 0 : 1 }
          : x
      )
    );

    this.service.toggleStatus(item.id).subscribe({

      next: (res) => {

        // Sync with server response
        this.items.update(list =>
          list.map(x =>
            x.id === res.id
              ? { ...x, status: res.status }
              : x
          )
        );

      },

      error: (err) => {

        console.error('Toggle failed:', err);

        // Rollback
        this.items.update(list =>
          list.map(x =>
            x.id === item.id
              ? { ...x, status: originalStatus }
              : x
          )
        );

        this.errorMsg.set(
          err?.error?.message ??
          'Failed to toggle status.'
        );

      }

    });

  }


  // ============================================================
  // Delete
  // ============================================================

  askDelete(item: CookingType): void {

    this.pendingDelete.set(item);

    this.showDeleteConfirm.set(true);

  }


  closeDeleteConfirm(): void {

    this.showDeleteConfirm.set(false);

    this.pendingDelete.set(null);

  }


  confirmDelete(): void {

    const item = this.pendingDelete();

    if (!item) return;

    this.service.delete(item.id).subscribe({

      next: () => {

        this.closeDeleteConfirm();

        this.load();

      },

      error: (err) => {

        console.error('Delete failed:', err);

        this.errorMsg.set(
          err?.error?.message ??
          'Failed to delete. Please try again.'
        );

        this.closeDeleteConfirm();

      }

    });

  }


  // ============================================================
  // Helpers
  // ============================================================

  get nameArControl() {
    return this.form.get('name_ar');
  }

  get nameEnControl() {
    return this.form.get('name_en');
  }


  private normalizeArabicText(text: string): string {

    return text
      .replace(/[أآإ]/g, 'ا')
      .replace(/ة/g, 'ه')
      .replace(/[ى]/g, 'ي')
      .replace(/[ؤ]/g, 'و')
      .replace(/[ئ]/g, 'ي');

  }

}